import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SITUATIONS } from "../lib/situations";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const HERO_BG = "https://images.pexels.com/photos/31602311/pexels-photo-31602311.jpeg";

export default function SituationPage() {
  const params = useParams();
  const { pathname } = useLocation();
  // Literal routes (e.g. /stop-foreclosure) don't expose a :slug param, so fall
  // back to deriving the slug from the pathname.
  const slug = params.slug || pathname.replace(/^\/+/, "").split("/")[0];
  const cfg = SITUATIONS[slug];
  if (!cfg) return <Navigate to="/" replace />;

  return (
    <>
      <section className="relative overflow-hidden" data-testid={`situation-hero-${cfg.slug}`}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} aria-hidden />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 bg-money/90 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5">
              {cfg.situation} • Nationwide
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{cfg.h1}</h1>
            <p className="mt-5 text-lg sm:text-xl text-white/85 max-w-2xl">{cfg.subh}</p>
            <ul className="mt-7 space-y-2 text-white/95">
              {cfg.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2"><CheckCircle2 size={20} className="text-money shrink-0 mt-1" /> {b}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div
              className="bg-white/95 backdrop-blur rounded-md border border-white/20 shadow-xl p-7 sm:p-8 text-navy"
              data-testid={`situation-cta-card-${cfg.slug}`}
            >
              <p className="text-money font-bold uppercase tracking-widest text-xs mb-2">Free • No Obligation</p>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight">
                Get a cash offer for your {cfg.situation.toLowerCase()} property
              </h2>
              <p className="mt-3 text-navy-800/80 text-sm sm:text-base">
                Two-minute request. We'll call within 24 hours with a firm cash offer.
              </p>
              <Link
                to={`/request-offer?src=${encodeURIComponent(cfg.slug)}&situation=${encodeURIComponent(cfg.situation)}`}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-money hover:bg-money/90 text-white text-base sm:text-lg font-bold uppercase tracking-wide px-6 py-4 rounded-md transition"
                data-testid={`situation-get-offer-btn-${cfg.slug}`}
              >
                Get My Cash Offer <ArrowRight size={20} />
              </Link>
              <p className="mt-4 text-xs text-navy-800/60 text-center">
                No fees. No repairs. Flexible closing date.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-offwhite">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-navy mb-8">Questions we hear about {cfg.situation.toLowerCase()} sales</h2>
          <Accordion type="single" collapsible className="w-full">
            {cfg.faq.map((f, i) => (
              <AccordionItem key={f.q} value={`sit-${i}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-navy">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-navy-800">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 text-center">
            <Link to="/" className="text-money font-semibold">← Back to home</Link>
          </div>
        </div>
      </section>
    </>
  );
}
